import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer
} from 'recharts';
import { Zap, DollarSign, Star, Clock } from 'lucide-react';
import type { ModelType } from '@/types/ai-admin';

interface ModelComparison {
  model: ModelType;
  queries: number;
  avgCost: number;
  avgResponseTime: number;
  avgRating: number;
}

interface ModelComparisonChartProps {
  models: ModelComparison[];
}

export default function ModelComparisonChart({ models }: ModelComparisonChartProps) {
  // Calculate totals for percentages
  const totalQueries = models.reduce((sum, m) => sum + m.queries, 0);

  // Format model names for display
  const formatModelName = (model: ModelType) => {
    if (model.includes('haiku')) {
return 'Claude Haiku 4.5';
}
    if (model.includes('sonnet')) {
return 'Claude Sonnet 4.5';
}
    if (model.includes('opus')) {
return 'Claude Opus 4.1';
}
    return model;
  };

  // Prepare radar chart data
  const radarData = [
    {
      metric: 'Speed',
      Haiku: 100,
      Sonnet: 70,
      Opus: 40
    },
    {
      metric: 'Quality',
      Haiku: 70,
      Sonnet: 85,
      Opus: 100
    },
    {
      metric: 'Cost Efficiency',
      Haiku: 100,
      Sonnet: 60,
      Opus: 20
    },
    {
      metric: 'Usage',
      Haiku: (models.find(m => m.model.includes('haiku'))?.queries || 0) / totalQueries * 100,
      Sonnet: (models.find(m => m.model.includes('sonnet'))?.queries || 0) / totalQueries * 100,
      Opus: (models.find(m => m.model.includes('opus'))?.queries || 0) / totalQueries * 100
    },
    {
      metric: 'Rating',
      Haiku: (models.find(m => m.model.includes('haiku'))?.avgRating || 0) * 20,
      Sonnet: (models.find(m => m.model.includes('sonnet'))?.avgRating || 0) * 20,
      Opus: (models.find(m => m.model.includes('opus'))?.avgRating || 0) * 20
    }
  ];

  // Get model badge color
  const getModelBadgeColor = (model: ModelType) => {
    if (model.includes('haiku')) {
return 'bg-blue-100 text-blue-800';
}
    if (model.includes('sonnet')) {
return 'bg-purple-100 text-purple-800';
}
    if (model.includes('opus')) {
return 'bg-orange-100 text-orange-800';
}
    return 'bg-gray-100 text-gray-800';
  };

  // Calculate recommendations
  const haikuModel = models.find(m => m.model.includes('haiku'));
  const sonnetModel = models.find(m => m.model.includes('sonnet'));
  const potentialSavings = sonnetModel ?
    (sonnetModel.queries * 0.3 * (sonnetModel.avgCost - (haikuModel?.avgCost || 0.002))) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Model Performance Comparison</CardTitle>
        <CardDescription>
          Compare speed, cost, and quality across AI models
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Radar Chart */}
        <div className="h-[250px]">
          <ResponsiveContainer width="100%" height={250}>
            <RadarChart data={radarData}>
              <PolarGrid className="stroke-muted" />
              <PolarAngleAxis dataKey="metric" className="text-xs" />
              <PolarRadiusAxis angle={90} domain={[0, 100]} className="text-xs" />
              <Radar
                name="Haiku"
                dataKey="Haiku"
                stroke="hsl(var(--chart-1))"
                fill="hsl(var(--chart-1))"
                fillOpacity={0.3}
              />
              <Radar
                name="Sonnet"
                dataKey="Sonnet"
                stroke="hsl(var(--chart-2))"
                fill="hsl(var(--chart-2))"
                fillOpacity={0.3}
              />
              <Radar
                name="Opus"
                dataKey="Opus"
                stroke="hsl(var(--chart-3))"
                fill="hsl(var(--chart-3))"
                fillOpacity={0.3}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Model Stats Table */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead className="text-center">Queries</TableHead>
              <TableHead className="text-center">Avg Cost</TableHead>
              <TableHead className="text-center">Avg Time</TableHead>
              <TableHead className="text-center">Rating</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {models.map((model) => (
              <TableRow key={model.model}>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className={getModelBadgeColor(model.model)}>
                      {formatModelName(model.model).split(' ')[1]}
                    </Badge>
                    <span className="text-sm">{formatModelName(model.model).split(' ').slice(2).join(' ')}</span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="space-y-1">
                    <span className="text-sm font-medium">{model.queries}</span>
                    <Progress
                      value={(model.queries / totalQueries) * 100}
                      className="h-1"
                    />
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <DollarSign className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">{model.avgCost.toFixed(4)}</span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">{(model.avgResponseTime / 1000).toFixed(1)}s</span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <Star className="h-3 w-3 text-yellow-500" />
                    <span className="text-sm">{model.avgRating.toFixed(1)}</span>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Optimization Recommendation */}
        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/10">
          <div className="flex items-start space-x-2">
            <Zap className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Model Optimization Opportunity
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-200">
                Route {Math.floor((sonnetModel?.queries || 0) * 0.3)} simple queries from Sonnet to Haiku
                to save ${potentialSavings.toFixed(2)}/month while maintaining quality.
              </p>
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span>Current distribution:</span>
                  <span>Optimal distribution:</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden flex">
                    {models.map((model, index) => (
                      <div
                        key={index}
                        className={`h-full ${
                          model.model.includes('haiku') ? 'bg-blue-500' :
                          model.model.includes('sonnet') ? 'bg-purple-500' :
                          'bg-orange-500'
                        }`}
                        style={{ width: `${(model.queries / totalQueries) * 100}%` }}
                      />
                    ))}
                  </div>
                  <span className="text-xs">→</span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden flex">
                    <div className="h-full bg-blue-500" style={{ width: '60%' }} />
                    <div className="h-full bg-purple-500" style={{ width: '35%' }} />
                    <div className="h-full bg-orange-500" style={{ width: '5%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}